<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter, RouterLink } from 'vue-router'
import { NPopconfirm, NSkeleton } from 'naive-ui'
import { useMemoryEntriesStore, GLOBAL_PROJECT, type MemoryEntryDetail } from '../stores/memory_entries'
import { useWikiMarkdown } from '../composables/useWikiMarkdown'
import { useDocumentTitle } from '../composables/useDocumentTitle'
import MemoryBrowse from './MemoryBrowse.vue'
import { memoryTypeTone, shortDate } from './MemoryEntryMeta'

// Full-page memory entry at `/memory/:project/:name`. Renders inside
// HomeView's shell, in the detail column of the shared Memory browser
// (scope + entries columns beside it on wide screens). Frontmatter shows
// as metadata chips, the description as a serif lede, the body as
// reading text. Carries the owner-only Edit (the wiki editor on the
// entry's file), Move and Delete actions; Move follows the entry, Delete
// returns to the `/memory` list.

const route = useRoute()
const router = useRouter()
const store = useMemoryEntriesStore()
const wikiMarkdown = useWikiMarkdown()

const param = (value: string | string[] | undefined): string => (Array.isArray(value) ? value[0] : value) ?? ''
const project = computed(() => param(route.params.project))
const name = computed(() => param(route.params.name))

const detail = ref<MemoryEntryDetail | null>(null)
const loading = ref(false)
const actionError = ref<string | null>(null)
const submitting = ref(false)
const moveTarget = ref<string | null>(null)

useDocumentTitle(computed(() => detail.value?.title || detail.value?.name || null))

// Target scopes for Move, as the raw slugs the scope column shows.
const moveOptions = computed(() => {
  const slugs = new Set<string>([GLOBAL_PROJECT])
  for (const group of store.groups) slugs.add(group.project)
  return [...slugs].filter((slug) => slug !== project.value)
})
const selected = computed(() => ({ project: project.value, name: name.value }))
const metaChips = computed(() => {
  const d = detail.value
  if (!d) return []
  return [
    { key: 'scope', value: d.project },
    { key: 'updated', value: shortDate(d.updated_at) },
    { key: 'created', value: shortDate(d.created_at) },
  ].filter((chip) => chip.value)
})
const renderedBody = computed(() => (detail.value ? wikiMarkdown.render(detail.value.body, detail.value.path) : ''))

watch(
  [project, name],
  async ([currentProject, currentName]) => {
    detail.value = null
    actionError.value = null
    moveTarget.value = null
    loading.value = true
    try {
      const fetched = await store.fetchEntry(currentName, currentProject)
      if (currentProject === project.value && currentName === name.value) detail.value = fetched
    } catch (e) {
      actionError.value = e instanceof Error ? e.message : 'Failed to load entry'
    } finally {
      loading.value = false
    }
  },
  { immediate: true },
)

async function run(action: () => Promise<void>, destination: string): Promise<void> {
  submitting.value = true
  actionError.value = null
  try {
    await action()
    await router.push(destination)
  } catch (e) {
    actionError.value = e instanceof Error ? e.message : 'Action failed'
  } finally {
    submitting.value = false
  }
}

function onDelete(): void {
  void run(() => store.deleteEntry(name.value, project.value), '/memory')
}
function onMove(): void {
  const target = moveTarget.value
  if (!target) return
  void run(
    () => store.moveEntry(name.value, project.value, target),
    `/memory/${encodeURIComponent(target)}/${encodeURIComponent(name.value)}`,
  )
}
const ACTION_BTN =
  'inline-flex min-h-9 items-center rounded-lg border border-border-control px-3 text-[13px] text-ink no-underline transition-colors hover:bg-surface-hover disabled:opacity-50 max-sm:min-h-11'
</script>

<template>
  <MemoryBrowse :selected="selected">
    <article class="flex flex-col gap-[18px]" data-testid="memory-entry-page">
      <p v-if="actionError" class="m-0 text-sm text-status-alert" data-testid="memory-entry-error">{{ actionError }}</p>

      <div v-if="loading" class="flex flex-col gap-2" data-testid="memory-entry-loading">
        <NSkeleton text :repeat="6" />
      </div>

      <template v-else-if="detail">
        <div class="flex flex-wrap items-center gap-x-3 gap-y-2 text-[13px] text-ink-faint">
          <span class="min-w-40 flex-1 font-mono">memory / {{ detail.project }} /</span>
          <div class="flex flex-wrap items-center gap-2">
            <RouterLink :to="`/p/${detail.path}/edit`" :class="ACTION_BTN" data-testid="memory-edit-link"
              >Edit</RouterLink
            >
            <label class="flex">
              <span class="sr-only">Move to scope</span>
              <select
                v-model="moveTarget"
                :class="[ACTION_BTN, 'bg-surface-bg', { 'text-ink-faint': !moveTarget }]"
                data-testid="memory-move-select"
              >
                <option :value="null" disabled>Move to scope…</option>
                <option v-for="slug in moveOptions" :key="slug" :value="slug">{{ slug }}</option>
              </select>
            </label>
            <button
              type="button"
              :class="ACTION_BTN"
              :disabled="!moveTarget || submitting"
              data-testid="memory-move-btn"
              @click="onMove"
            >
              Move
            </button>
            <NPopconfirm @positive-click="onDelete">
              <template #trigger>
                <button
                  type="button"
                  class="min-h-9 rounded-lg px-3 text-status-alert transition-colors hover:bg-status-alert/10 disabled:opacity-50 max-sm:min-h-11"
                  :disabled="submitting"
                  data-testid="memory-delete-btn"
                >
                  Delete
                </button>
              </template>
              Delete this memory entry? This can't be undone.
            </NPopconfirm>
          </div>
        </div>

        <h2
          class="m-0 break-words font-mono text-[22px] font-medium tracking-tight text-ink sm:text-[26px]"
          data-testid="memory-entry-title"
        >
          {{ detail.name }}
        </h2>

        <dl class="m-0 flex flex-wrap gap-2" data-testid="memory-entry-meta">
          <div
            class="flex min-h-[30px] items-center gap-1.5 rounded-lg border border-border-subtle bg-surface-raised px-2.5"
          >
            <dt class="text-xs text-ink-faint">type</dt>
            <dd class="m-0 rounded-full px-2 text-[13px] font-medium" :class="memoryTypeTone(detail.type)">
              {{ detail.type }}
            </dd>
          </div>
          <div
            v-for="chip in metaChips"
            :key="chip.key"
            class="flex min-h-[30px] items-center gap-1.5 rounded-lg border border-border-subtle bg-surface-raised px-2.5"
          >
            <dt class="text-xs text-ink-faint">{{ chip.key }}</dt>
            <dd class="m-0 text-[13px] text-ink" :class="{ 'font-mono': chip.key === 'scope' }">{{ chip.value }}</dd>
          </div>
        </dl>

        <p
          v-if="detail.description"
          class="m-0 max-w-[680px] font-serif text-xl leading-normal text-ink-muted italic"
          data-testid="memory-entry-description"
        >
          {{ detail.description }}
        </p>

        <div
          class="wiki-markdown max-w-[680px] border-t border-border-divider pt-[18px] !text-lg"
          data-testid="memory-entry-body"
          v-html="renderedBody"
        />

        <RouterLink :to="`/p/${detail.path}`" class="self-start text-[13px] text-ink-faint no-underline hover:text-ink">
          Open raw page →
        </RouterLink>
      </template>
    </article>
  </MemoryBrowse>
</template>
